import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { BoltIcon, CheckIcon, EcoHostingIcon } from '../icons';

export type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'ngahere';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  children: ReactNode;
}

const TONE: Record<BadgeTone, string> = {
  success: 'text-success bg-success-tint',
  warning: 'text-warning bg-warning-tint',
  danger: 'text-danger bg-pohutukawa-tint',
  info: 'text-moana-ink bg-moana-tint',
  neutral: 'text-muted bg-hover',
  ngahere: 'text-ngahere bg-ngahere-tint',
};

/** Status pill — booking state on the trips dashboard, rank on the eco page. */
export function Badge({ tone = 'neutral', className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-[11px] py-[5px]',
        'text-mini font-bold whitespace-nowrap',
        TONE[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/**
 * "ID verified", "Phone verified" — the trust markers on a driver card and on
 * the member's own profile.
 */
export function VerifiedBadge({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-ngahere-line bg-ngahere-tint',
        'px-[11px] py-[5px] text-caption font-semibold text-ngahere',
        className,
      )}
      {...rest}
    >
      <CheckIcon size={13} strokeWidth={2.2} />
      {children}
    </span>
  );
}

/** The lightning pill on rides that can be booked without driver approval. */
export function InstantBadge({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-pohutukawa-tint px-2 py-[3px]',
        'text-tiny font-bold text-pohutukawa',
        className,
      )}
      {...rest}
    >
      <BoltIcon size={11} filled />
      Instant
    </span>
  );
}

/** somsorium EcoTechFarms attribution pill on the home page's hosting strip. */
export function CarbonAwareBadge({
  className,
  children = 'Carbon-aware hosting',
  ...rest
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-eco-line px-3 py-1.5',
        'text-caption font-semibold whitespace-nowrap text-eco',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/** The inline "Powered by somsorium EcoTechFarms" line in the footer. */
export function EcoHostingNote({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-caption font-semibold text-eco',
        className,
      )}
      {...rest}
    >
      <EcoHostingIcon size={14} strokeWidth={1.8} />
      Powered by somsorium EcoTechFarms
    </span>
  );
}

/** Passenger / Driver role marker on a trip row. */
export function RoleBadge({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full bg-hover px-[9px] py-[3px]',
        'text-tiny font-semibold text-muted',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
